<template>
  <span :title="tag.mt_title" :aria-label="tag.mt_title"
        :class="containerClasses" class="menu--tag"></span>
</template>

<script lang="ts">
/**
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 1.2.2
 * @copyright  Copyright (C) 2022-2025, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 *
 *    2022-03-10 (1.0.0) (jhoward)
 *        - Refactored tag from section components.
 *
 *    2023-03-23 (1.0.1) (jhoward)
 *        - Add support for "Prime" tag in <menu-tag>.
 *        - Add support for "Fancy Glass" tag in <menu-tag>.
 *        - Add support for "Ice Glass" tag in <menu-tag>.
 *        - Add support for "Martini Glass" tag in <menu-tag>.
 *        - Add support for "Mason Jar" tag in <menu-tag>.
 *        - Add support for "Sup Glass" tag in <menu-tag>.
 *
 *    2023-03-27 (1.0.2) (jhoward)
 *        - Make the new tags in <menu-tag> use a 96x96px image rather than 128x128px.
 *        - Make the "Prime" tag in <menu-tag> use a 256x256px image.
 *
 *    2023-04-24 (1.1.0) (jhoward)
 *      - Add TypeScript support to <menu-tag>.
 *
 *    2023-10-18 (1.1.1) (zsahs)
 *      - Add support for "Coupe" tag in <menu-tag>.
 *
 *    2024-04-29 (1.1.2) (jhoward)
 *      - Add aria-label attribute in <menu-tag>.
 *
 *    2024-07-16 (1.2.0) (jhoward)
 *      - Give all <menu-tag> a generic .menu--tag class rather than different classes based on visual/textual menu containers.
 *
 *    2024-08-28 (1.2.1) (jhoward)
 *      - Fix some tag CSS for tags with circles around them in <menu-tag>.
 *
 *    2025-02-10 (1.2.2) (jhoward)
 *      - Add "Prime Black and White" tag to <menu-tag>.
 */

import {defineComponent, PropType} from "vue";
import {MenuTagModel} from "./defs";


export default defineComponent({
  name: 'menu-tag',
  props: {
    tag: {
      type: Object as PropType<MenuTagModel>,
      required: true
    },
    menuType: {
      type: String,
      default: 'visual',
      validator(value: string) {
        return ['visual', 'textual'].includes(value);
      }
    },
  },
  computed: {
    containerClasses(): {[key: string]: boolean} {
      const classes: {[key: string]: boolean} = {
        'tooltip-dock-bottom': this.menuType === 'visual',
        'tooltip-dock-right': this.menuType === 'textual',
      };
      classes[this.tag.mt_icon_class] = true;
      return classes;
    }
  }
})
</script>

<style scoped>
.menu--tag {
  position: relative;
  width: 1em;
  height: 1em;
  line-height: 1em;
  display: inline-block;
  vertical-align: middle;
  text-align: center;
  margin: 0 0.5em;
  background-size: contain;
  background-position: 50% 50%;
  background-repeat: no-repeat;
}

.menu--tag.tag-gluten-free::before,
.menu--tag.tag-raw_or_undercooked::before,
.menu--tag.tag-hot_and_spicy::before {
  position: absolute;
  top: -1px;
  left: -1px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid #000;
  line-height: 2em;
}

.menu--tag.tag-raw_or_undercooked::before,
.menu--tag.tag-hot_and_spicy::before{
  border: none;
  width: 24px;
  height: 24px;
}

.menu--tag.tag-gluten-free::before {
  content: 'GF';
  font-size: 14px;
  font-weight: 600;
}

.menu--tag.tag-raw_or_undercooked::before {
  content: '*';
  font-size: 2em;
}

.menu--tag.tag-hot_and_spicy::before {
  content: '**';
  font-size: 2em;
}

.menu--tag.tag-contains-nuts {
  background-image: url(/library/images/menu-tags/tag-contains-nuts-96.png);
}

.menu--tag.tag-spicy {
  background-image: url(/library/images/menu-tags/tag-spicy-96.png);
}

.menu--tag.tag-coupe {
  background-image: url(/library/images/menu-tags/tag-coupe-96.png);
}

.menu--tag.tag-certified-angus-beef {
  background-image: url(/library/images/menu-tags/tag-certified-angus-beef-96.png);
}

.menu--tag.tag-locals-favorite {
  background-image: url(/library/images/menu-tags/tag-locals-favorite-96.png);
}

.menu--tag.tag-healthier-options-available {
  background-image: url(/library/images/menu-tags/tag-healthier-options-available-96.png);
}

.menu--tag.tag-niman-ranch {
  background-image: url(/library/images/menu-tags/tag-niman-ranch-128.png);
}

.menu--tag.tag-prime {
  background-image: url(/library/images/menu-tags/tag-prime-256.png);
}

.menu--tag.tag-prime-bnw {
  background-image: url(/library/images/menu-tags/tag-prime-bnw-256.png);
}

.menu--tag.tag-fancy-glass {
  background-image: url(/library/images/menu-tags/tag-fancy-glass-96.png);
}

.menu--tag.tag-ice-glass {
  background-image: url(/library/images/menu-tags/tag-ice-glass-96.png);
}

.menu--tag.tag-martini-glass {
  background-image: url(/library/images/menu-tags/tag-martini-glass-96.png);
}

.menu--tag.tag-mason-jar {
  background-image: url(/library/images/menu-tags/tag-mason-jar-96.png);
}

.menu--tag.tag-sip-glass {
  background-image: url(/library/images/menu-tags/tag-sip-glass-96.png);
}

.menu--tag[data-tooltip]::after {
  white-space: nowrap;
}
</style>